/** SUNTRONIX ONE — AppleCare+ Transaction Control Centre (Phase 1). */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Search, ChevronRight, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [["", "All"], ["NEW_REQUEST", "New request"], ["DEVICE_VERIFICATION", "Activation pending"],
  ["ACTIVATION_VERIFIED", "Awaiting invoice"], ["INVOICE_ISSUED", "Invoice issued"],
  ["PAYMENT_PENDING", "Payment pending"], ["PARTIALLY_PAID", "Partially paid"],
  ["PAYMENT_RECEIVED", "Payment received"], ["BANK_RECONCILED", "Reconciliation"],
  ["CLOSED_SUCCESSFULLY", "Closed"], ["COMPLETE_PAYMENT_PENDING", "Payment pending"]];
const money = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AppleCareControl() {
  const [tab, setTab] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    api.get(`/applecare-txn/transactions${tab ? `?status=${tab}` : ""}${q ? `${tab ? "&" : "?"}q=${encodeURIComponent(q)}` : ""}`)
      .then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [tab, q]);
  useEffect(() => { api.get("/applecare-txn/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { load(); }, [load]);
  const canCreate = meta?.permissions?.includes("create");

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="applecare-control-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Operations · Apple Services</div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">AppleCare+ Transaction Control</h1>
          <p className="text-sm text-slate-500 mt-1">Record and track every AppleCare+ transaction from request to closure.</p>
        </div>
        {canCreate && <Button onClick={() => setShowNew(true)} data-testid="ac-new-btn" className="bg-slate-900"><Plus className="h-4 w-4 mr-1.5" /> New AppleCare Transaction</Button>}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID, customer, serial, invoice, PO…" className="pl-8 h-9 w-72" data-testid="ac-search" />
        </div>
      </div>

      <div className="mt-3 flex gap-1 flex-wrap border-b border-slate-200">
        {TABS.map(([id, label]) => (
          <button key={id + label} onClick={() => setTab(id)} data-testid={`ac-tab-${id || "all"}`}
            className={`px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>
            {label}{data?.counts?.[id] ? ` (${data.counts[id]})` : ""}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0 ? (
          <div className="text-[13px] text-slate-400 border border-dashed rounded-lg py-10 text-center">No transactions.</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
                <th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Device</th><th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Next action</th><th className="px-3 py-2"></th></tr></thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(t.id)} data-testid={`ac-row-${t.code}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{t.code}</td>
                    <td className="px-3 py-2">{t.customer_name}</td>
                    <td className="px-3 py-2 text-slate-500">{t.product || t.serial_number || "—"}</td>
                    <td className="px-3 py-2"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{(t.status || "").replace(/_/g, " ")}</span></td>
                    <td className="px-3 py-2 text-[11px] text-indigo-700 font-medium">{(NEXT(t.status))}</td>
                    <td className="px-3 py-2 text-right"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && <NewTxnDialog meta={meta} onClose={() => setShowNew(false)} onDone={(id) => { setShowNew(false); load(); setOpen(id); }} />}
      {open && <Txn360 tid={open} meta={meta} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

const NEXT_MAP = { ACTIVATION_VERIFIED: "ADD INVOICE", INVOICE_REQUIRED: "ADD INVOICE", INVOICE_ISSUED: "SEND CUSTOMER EMAIL", INVOICE_SENT: "FOLLOW UP PAYMENT", PAYMENT_RECEIVED: "RECONCILE PAYMENT", BANK_RECONCILED: "CLOSE" };
function NEXT(s) { return NEXT_MAP[s] || (s === "CLOSED_SUCCESSFULLY" ? "—" : "CONTINUE"); }

function NewTxnDialog({ meta, onClose, onDone }) {
  const [f, setF] = useState({ customer_name: "", email: "", product: "", model: "", serial_number: "", apple_part_number: "", customer_po_number: "", plan_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = async () => {
    if (!f.customer_name.trim()) return toast.error("Customer required");
    setBusy(true);
    try { const { data } = await api.post("/applecare-txn/transactions", f); toast.success(`Created ${data.code}`); onDone(data.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ac-new-dialog">
        <DialogHeader><DialogTitle>New AppleCare Transaction · {meta?.next_code}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["customer_name", "Customer"], ["email", "Email"], ["product", "Product"], ["model", "Model"], ["serial_number", "Serial"], ["apple_part_number", "Apple part #"], ["customer_po_number", "Customer PO"]].map(([k, l]) => (
            <label key={k} className="block"><div className="text-[11px] text-slate-500 mb-1">{l}</div>
              <Input value={f[k]} onChange={(e) => set({ [k]: e.target.value })} data-testid={`ac-new-${k}`} /></label>
          ))}
          <label className="block"><div className="text-[11px] text-slate-500 mb-1">Plan</div>
            <select value={f.plan_id} onChange={(e) => set({ plan_id: e.target.value })} className="w-full border rounded h-9 px-2 text-[13px]" data-testid="ac-new-plan">
              <option value="">— select plan —</option>
              {(meta?.plans || []).map((p) => <option key={p.id} value={p.id}>{p.name || p.plan_name || p.part_number}</option>)}
            </select></label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900" data-testid="ac-new-save">{busy ? "Saving…" : "Create"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STEP_ACTIONS = {
  VERIFY: { path: "verify-device", body: { status: "ELIGIBLE", source: "GSX", evidence: "verified" }, label: "Mark device eligible" },
  APPROVE: { path: "customer-approval", body: { approved_by: "Customer PO", evidence_type: "PO" }, label: "Record customer approval" },
  ACTIVATE_REQ: { path: "request-activation", body: { method: "ASD" }, label: "Request activation" },
  ACTIVATE_DONE: { path: "complete-activation", body: {}, label: "Complete activation" },
  ACTIVATE_VERIFY: { path: "verify-activation", body: { coverage_start: "", coverage_end: "", evidence: "verified" }, label: "Verify activation" },
  EMAIL: { path: "send-completion-email", body: {}, label: "Send completion email (dm@)" },
  RECONCILE: { path: "reconcile", body: {}, label: "Mark bank reconciled" },
  CLOSE: { path: "close", body: {}, label: "Close transaction" },
};

function Txn360({ tid, meta, onClose }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/applecare-txn/transactions/${tid}`).then(({ data }) => setT(data)), [tid]);
  useEffect(() => { load(); }, [load]);
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/applecare-txn/transactions/${tid}/${path}`, body || {}); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const confirmPlan = async () => {
    const pid = window.prompt("Plan id (leave blank to keep current):", (t?.plan?.plan_id) || (meta?.plans?.[0]?.id || ""));
    if (pid) act("confirm-plan", { plan_id: pid });
  };
  const addInvoice = async () => {
    const num = window.prompt("Invoice number:", t.code);
    if (num === null) return;
    const amt = window.prompt("Invoice total (INR):", (t.plan?.total) || "");
    act("invoice", { invoice_number: num, invoice_total: Number(amt) || 0, gst: 0 });
  };
  const recordPayment = async () => {
    const amt = window.prompt("Payment amount (INR):", t.billing?.outstanding || "");
    if (amt) act("payment", { amount: Number(amt), mode: "bank_transfer", reference: window.prompt("UTR/reference:") || "" });
  };
  if (!t) return <Dialog open onOpenChange={onClose}><DialogContent><Loader2 className="h-5 w-5 animate-spin" /></DialogContent></Dialog>;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ac-360">
        <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">{t.code}
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{(t.status || "").replace(/_/g, " ")}</span></DialogTitle></DialogHeader>

        <div className="flex items-center gap-1 flex-wrap bg-slate-50 rounded-lg p-2" data-testid="ac-progress">
          {t.progress.map((p, i) => (
            <div key={p.key} className="flex items-center gap-1">
              <span className={`text-[10px] font-semibold px-1.5 py-1 rounded flex items-center gap-1 ${p.done ? "bg-emerald-100 text-emerald-800" : "bg-white text-slate-400 border"}`}>
                {p.done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}{p.key}</span>
              {i < t.progress.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300" />}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
          <div className="text-[12px]"><span className="text-slate-500">Next action:</span> <span className="font-semibold text-indigo-800" data-testid="ac-next-action">{t.next_action?.replace(/_/g, " ")}</span></div>
          <div className="flex gap-1.5 flex-wrap">
            <StepButtons t={t} act={act} confirmPlan={confirmPlan} addInvoice={addInvoice} recordPayment={recordPayment} busy={busy} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <Info label="Customer" v={`${t.customer_name}${t.email ? " · " + t.email : ""}`} />
          <Info label="Device" v={`${t.product || "—"} · ${t.serial_number || "—"}`} />
          <Info label="Plan" v={t.plan?.name || "—"} />
          <Info label="Eligibility" v={(t.eligibility?.status || "NOT_CHECKED").replace(/_/g, " ")} />
          <Info label="Activation verified" v={t.activation?.verified_at ? t.activation.verified_at.slice(0, 10) : "—"} />
          <Info label="Invoice" v={t.invoice_number || "—"} />
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[["Invoice total", t.billing.invoice_total], ["Received", t.billing.received], ["Outstanding", t.billing.outstanding]].map(([l, v]) => (
            <div key={l} className="border rounded p-2"><div className="text-[9px] uppercase text-slate-500">{l}</div><div className="text-[13px] font-mono font-semibold">{money(v)}</div></div>
          ))}
          <div className="border rounded p-2"><div className="text-[9px] uppercase text-slate-500">Payment</div><div className="text-[12px] font-semibold">{t.billing.status}</div></div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Closure checklist</div>
          <div className="grid grid-cols-3 gap-1">
            {Object.entries(t.closure_checklist).map(([k, v]) => (
              <div key={k} className={`text-[10px] px-1.5 py-1 rounded flex items-center gap-1 ${v ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-400"}`}>
                {v ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}{k.replace(/_/g, " ")}</div>
            ))}
          </div>
        </div>

        <details><summary className="text-[11px] text-slate-500 cursor-pointer">Audit trail ({(t.audit || []).length})</summary>
          <div className="mt-1 border rounded divide-y max-h-40 overflow-y-auto">
            {(t.audit || []).slice().reverse().map((a, i) => (
              <div key={i} className="px-2 py-1 text-[11px] flex justify-between"><span>{a.action} {a.reason ? `· ${a.reason}` : ""}</span><span className="text-slate-400">{a.by} · {(a.at || "").slice(0, 16).replace("T", " ")}</span></div>
            ))}
          </div>
        </details>
      </DialogContent>
    </Dialog>
  );
}

function StepButtons({ t, act, confirmPlan, addInvoice, recordPayment, busy }) {
  const B = (label, fn, tid) => <Button size="sm" disabled={busy} onClick={fn} data-testid={tid} className="h-7 text-[11px] bg-slate-900">{label}</Button>;
  const s = t.status;
  if (["NEW_REQUEST", "DEVICE_VERIFICATION"].includes(s)) return B("Verify device", () => act("verify-device", { status: "ELIGIBLE", source: "GSX", evidence: "verified" }), "ac-do-verify");
  if (s === "PLAN_CONFIRMED") return B("Get approval", () => act("customer-approval", { approved_by: "Customer PO", evidence_type: "PO" }), "ac-do-approve");
  if (s === "CUSTOMER_APPROVAL_PENDING") return B("Confirm plan", confirmPlan, "ac-do-plan");
  if (s === "CUSTOMER_APPROVED") return B("Request activation", () => act("request-activation", { method: "ASD" }), "ac-do-reqact");
  if (["ACTIVATION_REQUESTED", "ACTIVATION_IN_PROGRESS"].includes(s)) return B("Complete activation", () => act("complete-activation", {}), "ac-do-completeact");
  if (s === "ACTIVATED") return B("Verify activation", () => act("verify-activation", { evidence: "verified" }), "ac-do-verifyact");
  if (["ACTIVATION_VERIFIED", "INVOICE_REQUIRED"].includes(s)) return B("Add invoice", addInvoice, "ac-do-invoice");
  if (s === "INVOICE_ISSUED") return B("Send email", () => act("send-completion-email", {}), "ac-do-email");
  if (["INVOICE_SENT", "PAYMENT_PENDING", "PARTIALLY_PAID", "COMPLETE_PAYMENT_PENDING"].includes(s)) return B("Record payment", recordPayment, "ac-do-payment");
  if (s === "PAYMENT_RECEIVED") return B("Reconcile", () => act("reconcile", {}), "ac-do-reconcile");
  if (["BANK_RECONCILED", "READY_TO_CLOSE"].includes(s)) return B("Close", () => act("close", {}), "ac-do-close");
  return null;
}

function Info({ label, v }) { return <div><div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-[12px]">{v}</div></div>; }
